'use client';
import React from 'react';
import { ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';

interface AccuracyDonutProps {
  accuracy: number;
  correctWords: number;
  wrongWords: number;
  /** Render without the card frame — used when nested inside a larger sheet. */
  bare?: boolean;
}

export default function AccuracyDonut({
  accuracy,
  correctWords,
  wrongWords,
  bare = false,
}: AccuracyDonutProps) {
  const data = [{ name: 'Accuracy', value: accuracy, fill: 'var(--foreground)' }];

  const body = (
    <>
      <div className="flex items-center justify-between gap-3 mb-5">
        <span className="eyebrow">Accuracy</span>
        <span className="text-xs text-faint">correct vs incorrect words</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="relative">
          <ResponsiveContainer width={168} height={168}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="82%"
              outerRadius="98%"
              data={data}
              startAngle={90}
              endAngle={-270}
            >
              {/* A thin ink arc on a hairline track — a printed gauge, not a pie */}
              <RadialBar dataKey="value" cornerRadius={0} background={{ fill: 'var(--border)' }} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="font-serif stat-value text-foreground leading-none"
              style={{ fontSize: '2rem', fontWeight: 600, letterSpacing: '-0.02em' }}
            >
              {accuracy.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Legend as a small two-row table — figures right-aligned and tabular */}
        <dl className="w-full max-w-[220px] mt-7 text-sm">
          <div
            className="flex items-center justify-between py-2"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <dt className="text-muted-foreground">Correct words</dt>
            <dd className="font-mono stat-value text-foreground">{correctWords}</dd>
          </div>
          <div
            className="flex items-center justify-between py-2"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <dt className="text-muted-foreground">Wrong words</dt>
            <dd
              className="font-mono stat-value"
              style={{ color: wrongWords > 0 ? 'var(--incorrect)' : 'var(--foreground)' }}
            >
              {wrongWords}
            </dd>
          </div>
        </dl>
      </div>
    </>
  );

  if (bare) {
    return <div className="p-5 sm:p-6 h-full flex flex-col">{body}</div>;
  }

  return <div className="card-elevated p-5 sm:p-6 h-full flex flex-col">{body}</div>;
}
